import { PortalLayout } from '@/interfaces/layout/layout';
import { isParamString } from '@/shared/utils/router';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import { useTextingStore } from '@/stores/company/texting.store';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const NotFound = () => import('@/components/Layouts/NotFound.vue');
const AdminInnerSideNav = () => import('@/components/Navigation/inner-side-nav/AdminInnerSideNav.vue');
const IntegrationsInnerSideNav = () => import('@/components/Navigation/inner-side-nav/IntegrationsInnerSideNav.vue');
const UsersInnerSideNav = () => import('@/components/Navigation/inner-side-nav/UsersInnerSideNav.vue');
const SideNavMenu = () => import('@/components/Navigation/side-nav/SideNavMenu.vue');
const FileTabNav = () => import('@/components/Navigation/tab-nav/FileTabNav.vue');
const NavHeader = () => import('@/components/Navigation/tab-nav/NavHeader.vue');
const SettingsTabNav = () => import('@/components/Navigation/tab-nav/SettingsTabNav.vue');
const DisbursementHub = () => import('@/components/Views/Company/Disbursement/DisbursementHub/DisbursementHub.vue');
const DisbursementView = () => import('@/components/Views/Company/Disbursement/DisbursementView/DisbursementView.vue');
const InPersonBavPage = () => import('@/components/Views/Company/File/BankAccountVerification/InPersonBavPage.vue');
const FileDetail = () => import('@/components/Views/Company/File/FileDetail.vue');
const HexAnalytics = () => import('@/components/Views/Company/HexAnalytics.vue');
const MessageHub = () => import('@/components/Views/Company/Messages/MessageHub.vue');
const PayoffVerificationsPage = () => import('@/components/Views/Company/Payoff/PayoffVerificationsPage.vue');
const NewPayoffVerificationPage = () => import('@/components/Views/Company/Payoff/NewPayoffVerificationPage.vue');
const PayoffVerificationStatusPage = () => import('@/components/Views/Company/Payoff/PayoffVerificationStatusPage.vue');
const PayoffOrderPage = () => import('@/components/Views/Company/Payoff/PayoffOrderPage.vue');
const ProgressTrackerEditorPage = () => import('@/components/Views/Company/Trackers/ProgressTrackerEditorPage.vue');
const ProgressTrackersPage = () => import('@/components/Views/Company/Trackers/ProgressTrackersPage.vue');
const AccountDetails = () => import('@/employee-portal/accounts/AccountDetails.vue');
const AccountsList = () => import('@/employee-portal/accounts/AccountsList.vue');
const CreateAccount = () => import('@/employee-portal/accounts/CreateAccount.vue');
const EditAccount = () => import('@/employee-portal/accounts/EditAccount.vue');
const AccountPaymentSettings = () => import('@/employee-portal/accounts/payment-settings/AccountPaymentSettings.vue');
const AccountTwoWayTextingSettings = () => import('@/employee-portal/accounts/two-way-texting/AccountTwoWayTextingSettings.vue');
const AccountProgressTrackerSettings = () => import('@/employee-portal/accounts/AccountProgressTrackerSettings.vue');
const BranchDetails = () => import('@/employee-portal/branches/BranchDetails.vue');
const CreateBranch = () => import('@/employee-portal/branches/CreateBranch.vue');
const EditBranch = () => import('@/employee-portal/branches/EditBranch.vue');
const FailedPayments = () => import('@/employee-portal/failed-payment/FailedPayments.vue');
const IdentityVerificationReview = () => import('@/employee-portal/identity-verification-review/IdentityVerificationReview.vue');
const CreateTitle = () => import('@/employee-portal/titles/CreateTitle.vue');
const EditTitle = () => import('@/employee-portal/titles/EditTitle.vue');
const TitleDetails = () => import('@/employee-portal/titles/TitleDetails.vue');
const EmployeePortalApp = () => import('./EmployeePortalApp.vue');
const DisbursementApprovalHub = () =>
    import('@/components/Views/Company/Disbursement/DisbursementApprovalHub/DisbursementApprovalHub.vue');
const DocumentManagementPage = () => import('@/components/Components/DocumentManagement/DocumentManagementPage.vue');

declare module 'vue-router' {
    interface RouteMeta {
        layout?: PortalLayout;
    }
}

// 1- if we absorb other portals, we only need to adjust the routing to consider the domain
// 2- add specialized router configs as their own const and merge them in the resulting router instance
const EMPLOYEE_ROUTES: RouteRecordRaw[] = [
    {
        path: '/',
        component: EmployeePortalApp,
    },
    {
        name: '/user/home',
        path: '/accounts',
        component: AccountsList,
    },
    {
        path: '/accounts/create',
        component: CreateAccount,
    },
    {
        path: '/accounts/:accountId',
        component: AccountDetails,
    },
    {
        path: '/accounts/:accountId/edit',
        component: EditAccount,
    },
    {
        path: '/accounts/:accountId/payment-settings',
        component: AccountPaymentSettings,
    },
    {
        path: '/accounts/:accountId/two-way-texting',
        component: AccountTwoWayTextingSettings,
    },
    {
        path: '/accounts/:accountId/progress-tracker',
        component: AccountProgressTrackerSettings,
    },
    {
        path: '/titles/create',
        component: CreateTitle,
    },
    {
        path: '/titles/:titleId',
        component: TitleDetails,
    },
    {
        path: '/titles/:titleId/edit',
        component: EditTitle,
    },
    {
        path: '/branches/create',
        component: CreateBranch,
    },
    {
        path: '/branches/:branchId',
        component: BranchDetails,
    },
    {
        path: '/branches/:branchId/edit',
        component: EditBranch,
    },
    {
        path: '/id-verification-review',
        component: IdentityVerificationReview,
    },
    {
        path: '/payments/failedPayments',
        component: FailedPayments,
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: NotFound,
    },
];

const COMPANY_ROUTES: RouteRecordRaw[] = [
    {
        path: '/user/analytics',
        components: {
            default: HexAnalytics,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        path: '/user/document-management',
        name: 'document-management',
        components: {
            default: DocumentManagementPage,
            sideNav: SideNavMenu,
            header: NavHeader,
        },
        props: {
            header: { title: 'Document management' },
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        path: '/user/early-deposit',
        components: {
            sideNav: SideNavMenu,
            tabNav: FileTabNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Early deposit' },
        },
    },
    {
        path: '/user/home',
        components: {
            sideNav: SideNavMenu,
            tabNav: FileTabNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Files' },
        },
    },
    {
        name: 'file-detail',
        path: '/user/files/:id',
        components: {
            default: FileDetail,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        name: 'in-person-bav-form',
        path: '/user/files/:id/bank-account-verification/:clientFilePivotId',
        components: {
            default: InPersonBavPage,
        },
        meta: {
            layout: PortalLayout.BLANK,
        },
        async beforeEnter(to) {
            const featureFlagsStore = useFeatureFlagsStore();
            await featureFlagsStore.ensureLoaded();

            if (!featureFlagsStore.flags?.in_person_bav_enabled) {
                return `/user/files/${to.params.id}`;
            }
        },
    },
    {
        name: 'disbursements-form',
        path: '/user/files/:id/disbursements/drafts/:userId/:disbursementId?',
        components: {
            default: DisbursementHub,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
        async beforeEnter() {
            const featureFlags = useFeatureFlagsStore();
            await featureFlags.ensureLoaded();
        },
    },
    {
        path: '/user/files/:id/disbursements/:disbursementId',
        components: {
            default: DisbursementView,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        name: 'disbursement-approvals',
        path: '/user/files/:id/disbursements/approvals/:userId/:disbursementId?',
        components: {
            default: DisbursementApprovalHub,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        path: '/user/manage',
        components: {
            sideNav: SideNavMenu,
            tabNav: SettingsTabNav,
            innerSideNav: UsersInnerSideNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Settings' },
        },
    },
    {
        path: '/user/messages',
        components: {
            default: MessageHub,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
        async beforeEnter(to) {
            const featureFlags = useFeatureFlagsStore();
            // this check limits the flashing of the splash page while the feature flag is loading
            await featureFlags.ensureLoaded();

            if (featureFlags.flags?.texting === 'enabled') {
                const textingStore = useTextingStore();
                const { branchPhone, clientPhone, clientId } = to.query;

                if (isParamString(branchPhone) && isParamString(clientPhone)) {
                    textingStore.openRemoteConversation(
                        branchPhone,
                        clientPhone,
                        isParamString(clientId) ? Number(clientId) : undefined,
                    );
                    return { path: '/user/messages', query: {} }; // clear query params
                }
            }
            return true;
        },
    },
    {
        path: '/user/settings',
        components: {
            sideNav: SideNavMenu,
            tabNav: SettingsTabNav,
            innerSideNav: AdminInnerSideNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Settings' },
        },
    },
    {
        path: '/user/settings/integrations',
        components: {
            sideNav: SideNavMenu,
            tabNav: SettingsTabNav,
            innerSideNav: IntegrationsInnerSideNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Settings' },
        },
    },
    {
        path: '/user/settings/tracker-templates',
        name: 'progress-tracker-templates',
        components: {
            default: ProgressTrackersPage,
            sideNav: SideNavMenu,
            tabNav: SettingsTabNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Settings' },
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        path: '/user/settings/tracker-templates/:id',
        name: 'progress-tracker-editor',
        components: {
            default: ProgressTrackerEditorPage,
            sideNav: SideNavMenu,
        },
        meta: {
            layout: PortalLayout.SPA,
        },
    },
    {
        path: '/user/payoffs',
        redirect: '/user/payoffs/v1',
    },
    {
        path: '/user/payoffs/v1',
        async beforeEnter() {
            const featureFlags = useFeatureFlagsStore();
            await featureFlags.ensureLoaded();

            if (featureFlags.flags?.payoff_verifications_ui === 'new') {
                return '/user/payoffs/v2';
            }
        },
        children: [
            {
                path: '',
                components: {
                    sideNav: SideNavMenu,
                    header: NavHeader,
                },
                props: {
                    header: { title: 'Payoff verifications' },
                },
            },
            {
                path: 'view/:id',
                components: {
                    sideNav: SideNavMenu,
                    header: NavHeader,
                },
                props: {
                    header: { title: 'Payoff verifications' },
                },
            },
        ],
    },
    {
        path: '/user/payoffs/v2',
        async beforeEnter() {
            const featureFlags = useFeatureFlagsStore();
            await featureFlags.ensureLoaded();

            if (featureFlags.flags?.payoff_verifications_ui !== 'new') {
                return '/user/payoffs';
            }
        },
        children: [
            {
                path: '',
                components: {
                    default: PayoffVerificationsPage,
                    sideNav: SideNavMenu,
                },
                meta: { layout: PortalLayout.SPA },
            },
            {
                path: 'create',
                name: 'new-payoff-verification',
                components: { default: NewPayoffVerificationPage },
                meta: { layout: PortalLayout.BLANK },
            },
            {
                path: 'verifications/:id',
                name: 'payoff-verification-status',
                components: { default: PayoffVerificationStatusPage },
                meta: { layout: PortalLayout.BLANK },
            },
        ],
    },
    {
        path: '/user/payoffs/order-new',
        components: {
            default: PayoffOrderPage,
        },
        meta: {
            layout: PortalLayout.BLANK,
        },
        async beforeEnter() {
            const featureFlags = useFeatureFlagsStore();
            await featureFlags.ensureLoaded();
        },
    },
    {
        path: '/user/payoffs/:catchAll',
        redirect: '/user/payoffs',
    },
    {
        path: '/user/postclose',
        components: {
            sideNav: SideNavMenu,
            tabNav: FileTabNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Files' },
        },
    },
    {
        path: '/user/settings',
        components: {
            sideNav: SideNavMenu,
            tabNav: SettingsTabNav,
            innerSideNav: AdminInnerSideNav,
            header: NavHeader,
        },
        props: {
            header: { title: 'Settings' },
        },
    },
    {
        path: '/user/password',
        components: {
            sideNav: SideNavMenu,
            header: NavHeader,
        },
        props: {
            header: { title: 'Update password' },
        },
    },
];

export const buildEmployeePortalRouter = (basePath?: string) => {
    const router = createRouter({
        history: createWebHistory(basePath),
        routes: [...EMPLOYEE_ROUTES],
    });

    // Add route guard to check MFA status in the employee portal
    router.beforeEach((to, from, next) => {
        const userMfaValidated = window.options?.userMfaValidated;

        // Allow access to MFA-related routes
        if (to.path.startsWith('/mfa')) {
            next();
            return;
        }

        // Check if user has completed MFA
        if (!userMfaValidated) {
            // Redirect to MFA completion page
            window.location.href = '/mfa/send';
            return;
        }

        next();
    });

    return router;
};

export const buildCompanyPortalRouter = (basePath?: string) => {
    return createRouter({
        history: createWebHistory(basePath),
        routes: [...COMPANY_ROUTES],
    });
};
